import React from 'react';
import classNames from 'classnames';
import useMobileView from '@/hooks/ui/useMobileView';
import { useQueryProvider } from '@/modules/QueryProvider';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import type ConstituentSearchFilterParams from '../types/ConstituentSearchFilterParams';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import FilterChip from './FilterChip/FilterChip';
import SaveFilterPrompt from './SaveFilterPrompt';

const getKey = ({ field, operator, value }: AdvancedConstituentSearchFilter) =>
  [
    field,
    operator,
    Array.isArray(value) ? value.join(',') : String(value),
  ].join('-');

function FilterList({
  className = '',
  enableSavedFilters = false,
  disabled = false,
}: {
  className?: string;
  enableSavedFilters?: boolean;
  disabled?: boolean;
}) {
  const isMobile = useMobileView();
  const { query, updateQuery } =
    useQueryProvider<ConstituentSearchFilterParams>();

  const filters: AdvancedConstituentSearchFilter[] = React.useMemo(
    () => (Array.isArray(query.filters) ? query.filters : []),
    [query],
  );

  const advancedFilters: AdvancedConstituentSearchFilter[] = React.useMemo(
    () => (Array.isArray(query.advancedFilters) ? query.advancedFilters : []),
    [query],
  );

  const handleDelete = React.useCallback(
    (index: number, isAdvanced?: boolean) => () => {
      const newFilters = [...(isAdvanced ? advancedFilters : filters)];
      newFilters.splice(index, 1);
      updateQuery({
        filterName: null,
        [isAdvanced ? 'advancedFilters' : 'filters']: newFilters.length
          ? newFilters
          : null,
      });
    },
    [advancedFilters, filters, updateQuery],
  );

  const showPrompt =
    enableSavedFilters &&
    !!(filters.length || advancedFilters.length) &&
    !query.filterName;

  if (!filters.length && !advancedFilters.length) {
    return null;
  }

  return (
    <div
      className={classNames(className, 'overflow-x-hidden')}
      data-testid="filters-list"
    >
      <div className="w-full overflow-x-scroll hide-scrollbar">
        <div className="flex items-center gap-2 md:flex-wrap">
          {filters.map((item, i) => (
            <FilterChip
              key={getKey(item)}
              onDelete={handleDelete(i)}
              {...item}
              disabled={disabled}
            />
          ))}
          {advancedFilters.map((item, i) => (
            <FilterChip
              key={getKey(item)}
              onDelete={handleDelete(i, true)}
              {...item}
              isAdvanced
              disabled={disabled}
            />
          ))}
          {showPrompt && !isMobile && (
            <SaveFilterPrompt className="h-[34px] py-1" disabled={disabled} />
          )}
        </div>
      </div>
      {showPrompt && isMobile && <SaveFilterPrompt className="mr-auto pl-0" />}
    </div>
  );
}

export default FilterList;
